import {FC, useEffect, useState} from 'react'
import {useSelector} from 'react-redux'
import {isEmpty} from 'lodash'
import {imageBaseUrl} from 'src/cms/helpers/constants'
import {toAbsoluteUrl} from 'src/cms/helpers'
import {ISettingTypeState} from '../../siteSettings/components/settings'
import './amnilAuth.css'

const MARK_GRADIENT_ID = 'amnil-auth-mark-gradient'

// Onboarding "pass-through" mark on the dark brand panel. Same geometry as
// cms/layout/components/header/OnboardingLogo, but inlined with fixed white ink:
// OnboardingLogo reads useThemeMode(), and this layout also renders from
// AuthInit, which sits above the theme providers.
const ProductMark: FC = () => (
  <span className='amnil-auth__product' aria-label='Onboarding'>
    <svg viewBox='0 0 64 64' aria-hidden='true'>
      <defs>
        <linearGradient id={MARK_GRADIENT_ID} gradientUnits='userSpaceOnUse' x1='0' y1='0' x2='64' y2='64'>
          <stop offset='0' stopColor='#EE1A49' />
          <stop offset='1' stopColor='#FF5122' />
        </linearGradient>
      </defs>
      <circle cx='32' cy='32' r='23' fill='none' stroke='#FFFFFF' strokeWidth='9' />
      <path
        d='M0 32H49M42 23L53 32L42 41'
        fill='none'
        stroke='#2A2A52'
        strokeWidth='15'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M3 32H50M42 23L53 32L42 41'
        fill='none'
        stroke={`url(#${MARK_GRADIENT_ID})`}
        strokeWidth='6'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
    nboarding
  </span>
)

/**
 * The branded split-screen auth shell, themed after amniltech.com: navy brand
 * panel with the Amnil crimson -> orange accent on the left, bank logo + content
 * on the right. Styles live in `amnilAuth.css`, scoped under `.amnil-auth`.
 *
 * Shared by `AuthPage` (login, authenticator) and `AuthInit`'s profile-error
 * screen so both keep the exact same chrome. Note that `AuthInit` sits ABOVE
 * `ThemeProvider`/`GlobalStyles` in `App.tsx`, so anything rendered in here must
 * look right from static CSS alone — don't rely on the styled-components theme
 * overrides being live.
 */
const AuthBrandedLayout: FC = ({children}) => {
  const [logo, setLogo] = useState('')
  const [applicationName, setApplicationName] = useState('')
  const settingTypeData: ISettingTypeState = useSelector((state: any) => state.settingType)

  useEffect(() => {
    document.body.classList.add('bg-white')
    return () => {
      document.body.classList.remove('bg-white')
    }
  }, [])

  useEffect(() => {
    if (!isEmpty(settingTypeData?.backendData)) {
      settingTypeData?.backendData?.['Theme settings']?.forEach((item: any) => {
        if (item?.name === 'mainLogo') {
          setLogo(item?.value)
        }
      })

      settingTypeData?.backendData?.['Application Credentials']?.forEach((item: any) => {
        if (item?.name === 'applicationName') {
          setApplicationName(item?.value)
        }
      })
    }
  }, [settingTypeData])

  return (
    <div className='amnil-auth'>
      <aside className='amnil-auth__brand'>
        <span className='amnil-auth__grid' aria-hidden='true' />
        <span className='amnil-auth__orb amnil-auth__orb--ring' aria-hidden='true' />
        <span className='amnil-auth__orb amnil-auth__orb--ring-sm' aria-hidden='true' />
        <span className='amnil-auth__orb amnil-auth__orb--fill' aria-hidden='true' />
        <span className='amnil-auth__orb amnil-auth__orb--dot' aria-hidden='true' />

        <ProductMark />

        <div className='amnil-auth__hero'>
          <div className='amnil-auth__eyebrow'>
            {applicationName ? `Welcome to ${applicationName}` : 'Digital account opening'}
          </div>
          <h1 className='amnil-auth__headline'>
            Every account, <span>onboarded</span> without the paperwork.
          </h1>
          <p className='amnil-auth__lede'>
            One workspace for branch makers, checkers and central operations — screening, KYC
            and core-banking sync, verified before anything reaches CBS.
          </p>
          <ul className='amnil-auth__steps'>
            <li>
              <span>Strategize</span>
            </li>
            <li>
              <span>Execute</span>
            </li>
            <li>
              <span>Evolve</span>
            </li>
          </ul>
        </div>

        <div className='amnil-auth__footer'>
          <span>
            Powered by <strong>Amnil Technologies</strong>
          </span>
          <span className='amnil-auth__badge'>Since 2009</span>
          <span className='amnil-auth__badge'>ISO 27001:2022 Certified</span>
        </div>
      </aside>

      <main className='amnil-auth__panel'>
        <div className='amnil-auth__logo'>
          <img
            src={logo ? `${imageBaseUrl}/${logo}` : toAbsoluteUrl('/media/logos/global-ime-logo-svg.jpg')}
            alt={applicationName || 'Bank logo'}
          />
        </div>

        <div className='amnil-auth__body login-box-body'>{children}</div>

        <div className='amnil-auth__legal'>
          <span>© {new Date().getFullYear()} Amnil Technologies</span>
          <a href='https://www.amniltech.com/' target='_blank' rel='noopener noreferrer'>
            amniltech.com
          </a>
        </div>
      </main>
    </div>
  )
}

export default AuthBrandedLayout
